import { etapasSemLancamento } from '../../constants/schedule';
import { isPregnancyCheckOverdue } from '../helpers/statusHelpers';
import { entradaAEmitir } from './entrada';
import { diasEntre } from './prazos';

/**
 * O que está esperando alguém — no laboratório inteiro, de uma vez.
 *
 * O app já sabia de tudo isto. `etapasSemLancamento` existe desde que o
 * Pipeline ganhou o chip amarelo, e `entradaAEmitir` existe desde que a TE
 * passou a convidar a emitir a faturinha. O que faltava era um lugar onde a
 * pergunta pudesse ser feita SEM SABER EM QUE SERVIÇO PERGUNTAR.
 *
 * Porque era isso que estava acontecendo: o chip está na linha do serviço, e
 * para ver a linha é preciso abrir o Pipeline, rolar até ela e reparar. Quem
 * não desconfia não rola. O convite da entrada aparece uma vez, no salvamento
 * da TE, e quem fecha não vê de novo — o caminho que sobra é o Financeiro,
 * serviço por serviço.
 *
 * Por isso a lista é agrupada por AÇÃO — lançar, cobrar — e não por etapa.
 * Hoje 8 das 11 pendências de lançamento são TE, mas isso é a estação do ano;
 * "lançar" e "cobrar" sobrevivem à próxima.
 *
 * Ordena pelo mais VELHO, não pelo mais caro. O que envelhece é o que some:
 * a entrada de R$ 380 parada há 24 dias já foi esquecida uma vez, e a de
 * R$ 3.325 de ontem ainda está na cabeça de quem lançou.
 */

/**
 * Etapas que já deviam ter sido lançadas, uma linha por etapa.
 *
 * Uma por ETAPA e não por serviço porque a ação é por etapa: um serviço com a
 * OPU e a FIV paradas são dois lançamentos a fazer, e somá-los num item só
 * esconderia um deles.
 */
export const lancamentosPendentes = (services = [], hoje = new Date()) =>
  (services || [])
    .flatMap(service => etapasSemLancamento(service, hoje).map(etapa => ({
      service,
      chave: `${service.id}:${etapa.id}`,
      etapa: etapa.label,
      agendada: etapa.agendada,
      dias: etapa.diasSemLancamento,
    })))
    .sort((a, b) => b.dias - a.dias);

/**
 * Serviços com TE lançada cuja entrada ainda não foi cobrada.
 *
 * O critério é `entradaAEmitir`, o MESMO que decide se a TE oferece emitir ao
 * salvar — os dois têm que concordar, senão o painel cobraria o que a TE
 * considera decidido (taxa zerada de propósito, serviço já faturado).
 *
 * `dias` conta da TE, não da emissão: é da TE que o prazo de 21 dias corre, e
 * é a TE que diz há quanto tempo o dinheiro está parado.
 */
export const entradasAEmitir = (
  services = [], { laboratory = null, entradaEmitida = {}, hoje = new Date() } = {},
) => {
  const h = hoje.toISOString().slice(0, 10);

  return (services || [])
    .map(service => ({
      service,
      pendente: entradaAEmitir(service, laboratory, !!entradaEmitida[service.id]),
    }))
    .filter(({ pendente }) => pendente.deve)
    .map(({ service, pendente }) => {
      const te = service?.te_date ? String(service.te_date).slice(0, 10) : null;
      return {
        service,
        chave: service.id,
        te,
        quantidade: pendente.quantidade,
        valor: pendente.valor,
        dias: te ? diasEntre(te, h) : 0,
      };
    })
    .sort((a, b) => b.dias - a.dias);
};

/**
 * O DINHEIRO QUE PEDE AÇÃO: emitir a fatura, ou cobrar o que venceu.
 *
 * Os motivos vêm de `criticidadeDoServico`, que já os calcula e é testada, e
 * chegam de fora por isso mesmo: decidir aqui, a partir de `billed` e `valor`,
 * seria uma segunda definição do mesmo julgamento, livre para discordar da
 * primeira — e as duas apareceriam na mesma tela.
 *
 * `entrada_a_emitir` fica FORA: ela já é a lista `entradas`, contada pelo mesmo
 * critério que a TE usa para convidar. Deixá-la aqui contaria o mesmo trabalho
 * duas vezes no total.
 *
 * Sem `criticidade` a lista volta vazia, e isso é resposta: quem não pode abrir
 * a porta do financeiro não precisa da contagem do que há por lá.
 */
const MOTIVOS_DE_DINHEIRO = ['parcela_vencida', 'entrada_vencida', 'nao_faturado'];

export const pendenciasDeDinheiro = (services = [], criticidade = null) => {
  if (typeof criticidade !== 'function') return [];

  return (services || [])
    .flatMap(service => (criticidade(service)?.motivos || [])
      .filter(m => MOTIVOS_DE_DINHEIRO.includes(m?.tipo))
      .map(m => ({
        service,
        chave: `${service.id}:${m.tipo}`,
        tipo: m.tipo,
        texto: m.texto,
        // `nao_faturado` não tem idade: ele nasce crítico no dia em que a etapa
        // fecha, e não há "desde quando" gravado em lugar nenhum.
        dias: Number(m.dias) || 0,
      })))
    .sort((a, b) => b.dias - a.dias);
};

/**
 * As duas listas, o total e por quais serviços filtrar.
 *
 * `total` é o que decide se o resumo aparece. Resumo que fica no ar com zero
 * vira moldura, e moldura ninguém lê — é a mesma razão pela qual o convite da
 * entrada não reaparece a cada gravação da FIV.
 *
 * Os dois conjuntos de id existem porque o resumo FILTRA o quadro em vez de
 * repetir os nomes: é por eles que o Dashboard e a aba de Serviços recortam a
 * lista. Vêm daqui, e não de um `filter` na tela, para as duas abas
 * recortarem pelo mesmo critério que o resumo contou — senão o botão prometeria
 * onze e a lista mostraria outro tanto.
 *
 * `Set` e não array: a tela pergunta por serviço, dentro do laço da lista.
 */
export const pendenciasDoLaboratorio = (
  services = [],
  { laboratory = null, entradaEmitida = {}, hoje = new Date(), criticidade = null } = {},
) => {
  const lancamentos = lancamentosPendentes(services, hoje);
  const entradas = entradasAEmitir(services, { laboratory, entradaEmitida, hoje });
  const dinheiro = pendenciasDeDinheiro(services, criticidade);

  /*
   * `idsComAcao`: TUDO o que espera alguém, num conjunto só.
   *
   * É o que define a tela inicial. O quadro abria em "tudo" e oferecia botões
   * para procurar o que precisa de ação; agora ele ABRE no que precisa de ação,
   * e ver tudo é que virou o clique. Quem chega de manhã não deveria ter que
   * pedir a lista do próprio trabalho.
   *
   * A DG60 atrasada entra aqui apesar de não ter lista própria: ela não é
   * lançamento em falta — pode nem ter sido agendada —, é exame que passou do
   * prazo. Ficaria de fora do conjunto exatamente no serviço em que o embrião
   * está há mais tempo sem confirmação.
   */
  const comAcao = new Set([
    ...lancamentos.map(l => l.service?.id),
    ...entradas.map(e => e.service?.id),
    ...dinheiro.map(d => d.service?.id),
    ...(services || []).filter(isPregnancyCheckOverdue).map(s => s.id),
  ].filter(Boolean));

  return {
    lancamentos,
    entradas,
    dinheiro,
    valorParado: entradas.reduce((s, e) => s + (Number(e.valor) || 0), 0),
    total: lancamentos.length + entradas.length + dinheiro.length,
    idsComLancamento: new Set(lancamentos.map(l => l.service?.id).filter(Boolean)),
    idsComEntrada: new Set(entradas.map(e => e.service?.id).filter(Boolean)),
    idsComAcao: comAcao,
    // Serviços, e não tarefas: um serviço com duas etapas paradas é uma linha.
    servicosComAcao: comAcao.size,
  };
};

/**
 * Recorta a lista pelo que o resumo está mostrando.
 *
 * Um lugar só porque as duas abas fazem o mesmo recorte, e elas precisam
 * concordar: o número no botão é do laboratório inteiro, e é este filtro que
 * tem que entregar exatamente aquele número quando ninguém mais estiver
 * filtrando.
 *
 * Um serviço com DUAS etapas paradas conta duas vezes no resumo — são dois
 * lançamentos a fazer — e aparece UMA vez na lista, porque a lista é de
 * serviços. Os números não batem de propósito, e é por isso que o rótulo do
 * botão diz "etapas", não "serviços".
 */
export const filtraPorPendencia = (services = [], pendencias, tipo) => {
  if (tipo === 'acao') {
    return (services || []).filter(s => pendencias?.idsComAcao?.has(s.id));
  }
  if (tipo === 'lancamento') {
    return (services || []).filter(s => pendencias?.idsComLancamento?.has(s.id));
  }
  if (tipo === 'entrada') {
    return (services || []).filter(s => pendencias?.idsComEntrada?.has(s.id));
  }
  return services || [];
};
